import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Phone,
  ShieldCheck,
  Send,
  X,
  CheckCircle2,
  AlertTriangle,
  Info,
  Clock,
} from "lucide-react";

import { PageHeader } from "@/components/PageHeader";
import {
  ApiError,
  getPhoneConfig,
  getPendingPhoneConfigRequest,
  requestPhoneConfigChange,
  cancelPhoneConfigChange,
  type MerchantPhoneConfig,
  type PhoneConfigChangeRequest,
} from "@/lib/api";

type Method = "mpesa" | "orange" | "airtel";

const METHOD_LABEL: Record<Method, string> = {
  mpesa: "M-Pesa",
  orange: "Orange Money",
  airtel: "Airtel Money",
};

const METHOD_COLOR: Record<Method, string> = {
  mpesa: "#00A551",
  orange: "#FF7A00",
  airtel: "#E4002B",
};

type FormState = {
  method_mpesa: string;
  method_orange: string;
  method_airtel: string;
  method_mpesa_2: string;
  method_orange_2: string;
  method_airtel_2: string;
};

const EMPTY_FORM: FormState = {
  method_mpesa: "",
  method_orange: "",
  method_airtel: "",
  method_mpesa_2: "",
  method_orange_2: "",
  method_airtel_2: "",
};

// ---------------------------------------------------------------------------

export function ConfigurationPage() {
  const qc = useQueryClient();

  const configQuery = useQuery<MerchantPhoneConfig>({
    queryKey: ["phone-config"],
    queryFn: getPhoneConfig,
    retry: false,
  });

  const pendingQuery = useQuery<PhoneConfigChangeRequest | null>({
    queryKey: ["phone-config-pending"],
    queryFn: getPendingPhoneConfigRequest,
    retry: false,
  });

  const config = configQuery.data ?? null;
  const pending = pendingQuery.data ?? null;
  const dual = !!config?.dual_withdrawal_numbers;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Reset form whenever config changes (initial load or after a save)
  useEffect(() => {
    if (!config) return;
    setForm({
      method_mpesa: config.method_mpesa ?? "",
      method_orange: config.method_orange ?? "",
      method_airtel: config.method_airtel ?? "",
      method_mpesa_2: config.method_mpesa_2 ?? "",
      method_orange_2: config.method_orange_2 ?? "",
      method_airtel_2: config.method_airtel_2 ?? "",
    });
  }, [config]);

  // Auto-clear flash message
  useEffect(() => {
    if (!message) return;
    const id = setTimeout(() => setMessage(null), 5_000);
    return () => clearTimeout(id);
  }, [message]);

  const hasChanges = useMemo(() => {
    if (!config) return false;
    const changed =
      form.method_mpesa !== (config.method_mpesa ?? "") ||
      form.method_orange !== (config.method_orange ?? "") ||
      form.method_airtel !== (config.method_airtel ?? "");
    if (!dual) return changed;
    return (
      changed ||
      form.method_mpesa_2 !== (config.method_mpesa_2 ?? "") ||
      form.method_orange_2 !== (config.method_orange_2 ?? "") ||
      form.method_airtel_2 !== (config.method_airtel_2 ?? "")
    );
  }, [config, form, dual]);

  const disabled = !!pending || submitting;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!hasChanges) {
      setMessage({ type: "error", text: "Aucune modification détectée." });
      return;
    }
    setSubmitting(true);
    setMessage(null);
    try {
      await requestPhoneConfigChange({
        requested_method_mpesa: form.method_mpesa,
        requested_method_orange: form.method_orange,
        requested_method_airtel: form.method_airtel,
        requested_method_mpesa_2: dual ? form.method_mpesa_2 : "",
        requested_method_orange_2: dual ? form.method_orange_2 : "",
        requested_method_airtel_2: dual ? form.method_airtel_2 : "",
        reason: reason.trim() || undefined,
      });
      setReason("");
      setMessage({
        type: "success",
        text:
          "Demande envoyée. Vos anciens numéros restent actifs jusqu'à validation par l'administration.",
      });
      qc.invalidateQueries({ queryKey: ["phone-config-pending"] });
    } catch (err) {
      setMessage({ type: "error", text: errorText(err) });
    } finally {
      setSubmitting(false);
    }
  };

  const cancelPending = async () => {
    if (!pending?.id) return;
    if (!confirm("Annuler la demande de modification en cours ?")) return;
    try {
      await cancelPhoneConfigChange(pending.id);
      setMessage({ type: "success", text: "Demande annulée." });
      qc.invalidateQueries({ queryKey: ["phone-config-pending"] });
      // Restore current config in form
      if (config) {
        setForm({
          method_mpesa: config.method_mpesa ?? "",
          method_orange: config.method_orange ?? "",
          method_airtel: config.method_airtel ?? "",
          method_mpesa_2: config.method_mpesa_2 ?? "",
          method_orange_2: config.method_orange_2 ?? "",
          method_airtel_2: config.method_airtel_2 ?? "",
        });
      }
    } catch (err) {
      setMessage({ type: "error", text: errorText(err) });
    }
  };

  const isConfigNotAvailable = configQuery.isError;

  return (
    <div>
      <PageHeader
        title="Paramètres"
        breadcrumbs={[{ label: "Mon espace" }, { label: "Paramètres" }]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left column : form */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-title">Numéros de réception des retraits</div>
                <div className="card-subtitle">
                  Numéros mobile money vers lesquels vos retraits seront envoyés.
                  {dual && " Vous avez activé les numéros secondaires (2 par opérateur)."}
                </div>
              </div>
            </div>

            {configQuery.isLoading ? (
              <div className="card-body text-slate-500">Chargement…</div>
            ) : isConfigNotAvailable ? (
              <div className="card-body flex items-start gap-2 text-danger-700 text-sm">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <div>
                  La configuration n'a pas pu être chargée. Rechargez la page ou contactez le
                  support si le problème persiste.
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="card-body space-y-5">
                {pending && <PendingBanner pending={pending} onCancel={cancelPending} />}

                {(["mpesa", "orange", "airtel"] as Method[]).map((m) => (
                  <MethodRow
                    key={m}
                    method={m}
                    primary={form[`method_${m}` as `method_${Method}`]}
                    secondary={dual ? form[`method_${m}_2` as const] : undefined}
                    onPrimaryChange={(v) =>
                      setForm((f) => ({ ...f, [`method_${m}`]: v }))
                    }
                    onSecondaryChange={
                      dual
                        ? (v) => setForm((f) => ({ ...f, [`method_${m}_2`]: v }))
                        : undefined
                    }
                    disabled={disabled}
                  />
                ))}

                <div>
                  <label className="block text-sm font-semibold text-[#4a4a6a] mb-2">
                    Motif de la modification (optionnel)
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    disabled={disabled}
                    rows={2}
                    placeholder="Ex. Ancien numéro perdu, nouvel opérateur, etc."
                    className="form-input py-2 text-sm w-full"
                  />
                </div>

                {message && (
                  <div
                    className={`flex items-start gap-2 rounded-lg border p-3 text-xs ${
                      message.type === "success"
                        ? "border-success-100 bg-success-50 text-success-700"
                        : "border-danger-100 bg-danger-50 text-danger-700"
                    }`}
                  >
                    {message.type === "success" ? (
                      <CheckCircle2 size={14} className="shrink-0 mt-0.5" />
                    ) : (
                      <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                    )}
                    <div>{message.text}</div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={disabled || !hasChanges}
                  className="btn btn-primary w-full"
                >
                  <Send size={14} />
                  {submitting ? "Envoi…" : "Envoyer la demande de modification"}
                </button>

                {!!pending && (
                  <div className="text-xs text-slate-500 text-center">
                    Une demande est déjà en cours — annulez-la avant d'en soumettre une nouvelle.
                  </div>
                )}
              </form>
            )}
          </div>
        </div>

        {/* Right column : info */}
        <div className="space-y-5">
          <div className="card">
            <div className="card-body">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={16} className="text-brand-500" />
                <div className="font-semibold text-[#181c32] text-sm">
                  Pourquoi une validation ?
                </div>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Les numéros de réception protègent votre cash : chaque changement doit être approuvé
                par l'administration pour éviter les fraudes en cas de compromission de votre
                compte.
              </p>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">
                Configuration active
              </div>
              <ul className="space-y-2 text-sm">
                {(["mpesa", "orange", "airtel"] as Method[]).map((m) => (
                  <li key={m} className="flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ background: METHOD_COLOR[m] }}
                    />
                    <span className="text-slate-500">{METHOD_LABEL[m]}</span>
                    <span className="ml-auto font-mono text-xs text-[#181c32]">
                      {config?.[`method_${m}` as const] || "—"}
                    </span>
                  </li>
                ))}
                {dual && (
                  <>
                    <li className="pt-2 mt-2 border-t border-slate-100 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                      Numéros secondaires
                    </li>
                    {(["mpesa", "orange", "airtel"] as Method[]).map((m) => (
                      <li key={`${m}-2`} className="flex items-center gap-2">
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0 opacity-50"
                          style={{ background: METHOD_COLOR[m] }}
                        />
                        <span className="text-slate-500">{METHOD_LABEL[m]}</span>
                        <span className="ml-auto font-mono text-xs text-[#181c32]">
                          {config?.[`method_${m}_2` as const] || "—"}
                        </span>
                      </li>
                    ))}
                  </>
                )}
              </ul>
            </div>
          </div>

          <div className="rounded-lg bg-brand-50 border border-brand-100 p-3 text-xs text-brand-700 flex gap-2">
            <Info size={14} className="shrink-0 mt-0.5" />
            <div>
              Vos numéros doivent commencer par <strong>243</strong> (ou <strong>0</strong>) suivi
              du préfixe opérateur. Ex : 0820000000 pour Vodacom M-Pesa.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function MethodRow({
  method,
  primary,
  secondary,
  onPrimaryChange,
  onSecondaryChange,
  disabled,
}: {
  method: Method;
  primary: string;
  secondary?: string;
  onPrimaryChange: (v: string) => void;
  onSecondaryChange?: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <span
          className="w-2.5 h-2.5 rounded-full"
          style={{ background: METHOD_COLOR[method] }}
        />
        <span className="text-sm font-semibold text-[#181c32]">{METHOD_LABEL[method]}</span>
      </div>

      <div className={onSecondaryChange ? "grid grid-cols-1 sm:grid-cols-2 gap-3" : ""}>
        <PhoneInput
          label={onSecondaryChange ? "Numéro principal" : "Numéro"}
          value={primary}
          onChange={onPrimaryChange}
          disabled={disabled}
          placeholder="0820000000"
        />
        {onSecondaryChange && (
          <PhoneInput
            label="Numéro secondaire"
            value={secondary ?? ""}
            onChange={onSecondaryChange}
            disabled={disabled}
            placeholder="Optionnel"
          />
        )}
      </div>
    </div>
  );
}

function PhoneInput({
  label,
  value,
  onChange,
  disabled,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-slate-500 font-bold mb-1">
        {label}
      </label>
      <div className="relative">
        <Phone
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          type="tel"
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/\s/g, ""))}
          disabled={disabled}
          placeholder={placeholder}
          className="form-input pl-9 py-2 text-sm w-full font-mono"
          maxLength={20}
        />
      </div>
    </div>
  );
}

function PendingBanner({
  pending,
  onCancel,
}: {
  pending: PhoneConfigChangeRequest;
  onCancel: () => void;
}) {
  return (
    <div className="rounded-lg border border-warning-100 bg-warning-50 p-4">
      <div className="flex items-start gap-2 mb-2">
        <Clock size={16} className="text-warning-700 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-semibold text-warning-800 text-sm">
            Demande de modification en attente
          </div>
          <div className="text-xs text-warning-700 mt-0.5">
            Vos anciens numéros restent actifs jusqu'à validation par l'administration.
            {pending.created_at && (
              <>
                {" "}
                Soumise le{" "}
                {new Date(pending.created_at).toLocaleDateString("fr-FR", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                .
              </>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="text-warning-700 hover:text-warning-900 shrink-0"
          title="Annuler la demande"
        >
          <X size={14} />
        </button>
      </div>

      <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
        {(["mpesa", "orange", "airtel"] as Method[]).map((m) => {
          const v = pending[`requested_method_${m}` as const] as string | undefined;
          const v2 = pending[`requested_method_${m}_2` as const] as string | undefined;
          return (
            <div key={m} className="rounded bg-white/60 px-2 py-1.5 border border-warning-100">
              <div className="flex items-center gap-1.5">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: METHOD_COLOR[m] }}
                />
                <span className="text-[10px] uppercase tracking-wider text-warning-800 font-bold">
                  {METHOD_LABEL[m]}
                </span>
              </div>
              <div className="font-mono text-xs text-[#181c32] mt-0.5">{v || "—"}</div>
              {v2 && <div className="font-mono text-[11px] text-slate-500">2nd : {v2}</div>}
            </div>
          );
        })}
      </div>

      {pending.reason && (
        <div className="mt-2 text-[11px] text-warning-800 italic">
          Motif : {pending.reason}
        </div>
      )}
    </div>
  );
}

function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.detail || err.title || "Erreur";
  if (err instanceof Error) return err.message;
  return "Erreur inconnue";
}
